import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cccdAdmin, type PodiumResult } from '@/lib/api/cccdAdminClient';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { StatusPill } from '@/components/cccd/StatusPill';
import { TrashBinIcon } from '@/icons';
import { inputCls, btnSecondary } from '@/components/admin/FormComponents';
import Alert from '@/components/ui/alert/Alert';
import { applyOptimistic, rollbackOptimistic } from '@/lib/optimisticMutation';
import { showUndoToast } from '@/lib/undoToast';
import { toastError } from '@/lib/toast';
import { getErrorStatus } from '@/lib/errors';
import { LoadState } from "@/components/ui/LoadState"

interface PodiumResultsData {
  results: PodiumResult[];
}

type Target =
  | { type: 'event'; eventId: string; categories: { slug: string; name: string }[] }
  | { type: 'regional'; regionalId: string; categories: { slug: string; name: string }[] };

// Mensajes del API (en inglés) que el operador puede ver al registrar o borrar un podio.
const PODIUM_ERROR_MESSAGES: Record<string, string> = {
  'Team is not registered in this category': 'Este equipo no está inscrito en esta categoría.',
  'Team does not belong to this event': 'Este equipo no pertenece a este evento.',
  'Team does not belong to this regional event': 'Este equipo no pertenece a este evento regional.',
  'Category not found for this event': 'Esta categoría no existe en el evento.',
  'Category not found for this regional event': 'Esta categoría no existe en el evento regional.',
  'This position is already taken for this category': 'Ese lugar ya tiene un equipo registrado en esta categoría.',
  'Validation failed': 'Revisa los datos del podio e inténtalo de nuevo.',
};

function podiumErrorMessage(err: any, fallback: string): string {
  const raw: string | undefined = err?.response?.data?.error;
  if (!raw) return fallback;
  return PODIUM_ERROR_MESSAGES[raw] ?? raw;
}

const POSITIONS = [1, 2, 3] as const;
const POSITION_LABEL: Record<1 | 2 | 3, string> = { 1: '1°', 2: '2°', 3: '3°' };
const POSITION_DISCOUNT: Record<1 | 2 | 3, string> = { 1: '100%', 2: '75%', 3: '50%' };

// Exactamente 3 cupos fijos por categoría (1°/2°/3°), nunca una tabla abierta
// de resultados — cccd-ux-redesign, Podio.dc.html. "Registrar" expande el
// cupo vacío en línea (selector de equipo), sin modal.
export function PodiumResultsSection({ target, teams }: { target: Target; teams: { id: string; name: string; categoryId?: string }[] }) {
  const qc = useQueryClient();
  const parentId = target.type === 'event' ? target.eventId : target.regionalId;
  const queryKey = ['podium-results', parentId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      cccdAdmin.getResults(
        target.type === 'event' ? { eventId: target.eventId } : { regionalEventId: target.regionalId },
      ),
  });
  const results = data?.results ?? [];

  const [activeCategory, setActiveCategory] = useState(target.categories[0]?.slug ?? '');
  useEffect(() => {
    if (!activeCategory && target.categories[0]) setActiveCategory(target.categories[0].slug);
  }, [target.categories, activeCategory]);

  // Candidatos a ganador: solo equipos de la categoría elegida (el API rechaza con 400 a los de otra categoría).
  // En eventos nacionales se piden ya filtrados por categoría (hasta 200); en regionales y como respaldo se usa
  // la lista recibida, filtrada por categoryId cuando viene.
  const { data: categoryTeamsData } = useQuery({
    queryKey: ['podium-category-teams', parentId, activeCategory],
    queryFn: () => cccdAdmin.listTeams({ eventId: parentId, categoryId: activeCategory, limit: 200 }),
    enabled: target.type === 'event' && !!activeCategory,
  });
  const candidateTeams =
    target.type === 'event' && categoryTeamsData
      ? categoryTeamsData.teams.map((t) => ({ id: t.id, name: t.name }))
      : teams.filter((t) => !t.categoryId || t.categoryId === activeCategory);

  const [registeringPosition, setRegisteringPosition] = useState<1 | 2 | 3 | null>(null);
  const [teamId, setTeamId] = useState('');
  const [error, setError] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: (position: 1 | 2 | 3) =>
      target.type === 'event'
        ? cccdAdmin.createEventResult(target.eventId, { categorySlug: activeCategory, position, teamId })
        : cccdAdmin.createRegionalResult(target.regionalId, { categorySlug: activeCategory, position, teamId }),
    onSuccess: () => {
      setRegisteringPosition(null);
      setTeamId('');
      setError('');
      invalidate();
    },
    onError: (err: any) => setError(podiumErrorMessage(err, 'No se pudo registrar el podio. Inténtalo de nuevo.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (resultId: string) => cccdAdmin.deleteResult(resultId),
  });

  // Solo la eliminación va optimista (undo-toast): la creación depende de
  // datos del equipo (capitán, institución) que este componente no tiene
  // localmente, así que fabricar una fila optimista fiel arriesgaría mostrar
  // datos incompletos — ver design.md, tabla de Decisión 3.
  const handleDelete = async (result: PodiumResult) => {
    const previous = await applyOptimistic<PodiumResultsData>(qc, queryKey, (prev) =>
      prev ? { ...prev, results: prev.results.filter((r) => r.id !== result.id) } : prev,
    );
    showUndoToast({
      message: `Podio de "${result.team.name}" eliminado`,
      onUndo: () => rollbackOptimistic(qc, queryKey, previous),
      onCommit: async () => {
        try {
          await deleteMutation.mutateAsync(result.id);
          invalidate();
        } catch (err) {
          rollbackOptimistic(qc, queryKey, previous);
          // 409: el descuento ya se aplicó a un equipo; cualquier otro fallo se informa con su causa real.
          toastError(
            getErrorStatus(err) === 409
              ? 'No se pudo eliminar: este podio ya fue usado para un descuento.'
              : podiumErrorMessage(err, 'No se pudo eliminar el podio. Inténtalo de nuevo.'),
          );
        }
      },
    });
  };

  const categoryResults = results.filter((r) => r.categorySlug === activeCategory);
  const resultByPosition = (position: 1 | 2 | 3) => categoryResults.find((r) => r.position === position);

  return (
    <SectionHeader
      title="Podio por categoría"
      subtitle="1° lugar recibe 100%, 2° 75%, 3° 50% de descuento — un solo uso por resultado"
      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8M12 17v4M7 4h10l-1 7a4 4 0 0 1-8 0z" /><path d="M5 4h2v3a3 3 0 0 1-2 2.8M19 4h-2v3a3 3 0 0 0 2 2.8" /></svg>}
    >
      {target.categories.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Este evento no tiene categorías configuradas. Ve a la pestaña "Configuración" para añadirlas.</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {target.categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => { setActiveCategory(c.slug); setRegisteringPosition(null); }}
                className={`min-h-11 rounded-lg px-3.5 py-1.5 text-[13px] font-medium transition-colors sm:min-h-0 ${
                  activeCategory === c.slug
                    ? 'bg-gray-100 font-semibold text-gray-900 dark:bg-gray-800 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {isLoading ? (
            <LoadState />
          ) : (
            <div className="flex flex-col gap-2.5">
              {POSITIONS.map((position) => {
                const result = resultByPosition(position);
                if (result) {
                  return (
                    <div key={position} className="flex items-center justify-between rounded-xl border border-gray-100 px-4 py-3 dark:border-gray-800">
                      <div className="flex items-center gap-3">
                        <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-warning-50 text-xs font-bold text-warning-700 dark:bg-warning-500/15 dark:text-orange-400">
                          {POSITION_LABEL[position]}
                        </span>
                        <span>
                          <span className="block text-sm font-medium text-gray-900 dark:text-white">{result.team.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{result.discountPercent}% de descuento</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusPill tone={result.usedByTeamId ? 'brand' : 'gray'} label={result.usedByTeamId ? 'Descuento aplicado' : 'Sin usar'} />
                        {!result.usedByTeamId && (
                          <button
                            type="button"
                            onClick={() => handleDelete(result)}
                            title="Eliminar"
                            aria-label={`Eliminar el podio ${POSITION_LABEL[position]} de ${result.team.name}`}
                            className="flex size-11 items-center justify-center rounded-lg text-gray-500 hover:bg-error-50 hover:text-error-600 sm:size-8 dark:text-gray-400 dark:hover:bg-error-500/10"
                          >
                            <TrashBinIcon className="size-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }

                if (registeringPosition === position) {
                  return (
                    <div key={position} className="rounded-xl border border-brand-200 bg-brand-50/40 px-4 py-3 dark:border-brand-800 dark:bg-brand-500/5">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500 dark:bg-gray-800">
                          {POSITION_LABEL[position]}
                        </span>
                        <select
                          aria-label={`Equipo ganador del ${POSITION_LABEL[position]} lugar`}
                          value={teamId}
                          onChange={(e) => setTeamId(e.target.value)}
                          className={`${inputCls} min-w-[220px] flex-1`}
                        >
                          <option value="">{candidateTeams.length === 0 ? 'No hay equipos en esta categoría' : 'Seleccionar equipo ganador…'}</option>
                          {candidateTeams.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                        <button type="button" onClick={() => { setRegisteringPosition(null); setTeamId(''); setError(''); }} className={btnSecondary}>
                          Cancelar
                        </button>
                        <button
                          type="button"
                          disabled={!teamId || createMutation.isPending}
                          onClick={() => createMutation.mutate(position)}
                          className="min-h-11 rounded-lg bg-brand-500 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                        >
                          {createMutation.isPending ? 'Guardando…' : `Confirmar ${POSITION_DISCOUNT[position]}`}
                        </button>
                      </div>
                      {error && <div className="mt-2"><Alert variant="error" title="No se pudo registrar el podio" message={error} /></div>}
                    </div>
                  );
                }

                return (
                  <div key={position} className="flex items-center justify-between rounded-xl border-[1.5px] border-dashed border-gray-300 bg-gray-25 px-4 py-3 dark:border-gray-700 dark:bg-white/[0.02]">
                    <div className="flex items-center gap-3">
                      <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500 dark:text-gray-400 dark:bg-gray-800">
                        {POSITION_LABEL[position]}
                      </span>
                      <span className="text-[13px] text-gray-500 dark:text-gray-400">Sin resultado registrado</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setRegisteringPosition(position); setTeamId(''); setError(''); }}
                      className={btnSecondary}
                    >
                      Registrar
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </SectionHeader>
  );
}
